import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, NavLink, Outlet } from 'react-router-dom';
import { LayoutDashboard, Shield, Users, RefreshCcw, ScrollText } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';

export function AdminProtected({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'loading' | 'admin' | 'denied' | 'unconfigured'>('loading');

  useEffect(() => {
    let cancelled = false;
    api.admin.me()
      .then(({ is_admin, configured }) => {
        if (cancelled) return;
        if (!configured) setState('unconfigured');
        else if (is_admin) setState('admin');
        else setState('denied');
      })
      .catch(() => {
        if (!cancelled) setState('denied');
      });
    return () => { cancelled = true; };
  }, []);

  if (state === 'loading') {
    return (
      <div className="grid min-h-[12rem] place-items-center text-sm text-zinc-500">
        Verifying platform access…
      </div>
    );
  }

  if (state === 'unconfigured') {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <Shield className="mx-auto size-10 text-zinc-500" />
        <h1 className="mt-4 text-xl font-semibold text-white">Admin console not configured</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Set <code className="text-zinc-300">PLATFORM_ADMIN_USER_IDS</code> or{' '}
          <code className="text-zinc-300">PLATFORM_ADMIN_EMAILS</code> on the server, then restart.
        </p>
        <Link to="/dashboard" className="mt-6 inline-block text-sm text-cyan-400 hover:text-cyan-300">
          Back to dashboard
        </Link>
      </div>
    );
  }

  if (state === 'denied') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

const adminNav = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/users', label: 'All users', icon: Users, end: false },
  { to: '/admin/audit', label: 'Audit log', icon: ScrollText, end: false },
];

export default function AdminLayout() {
  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await api.admin.refresh();
      window.location.reload();
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-cyan-400">
            <Shield className="size-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Platform console</span>
          </div>
          <h1 className="mt-1 text-2xl font-semibold text-white">Operator dashboard</h1>
          <p className="mt-1 text-sm text-zinc-400">
            Cross-tenant monitoring for all app users and businesses.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="shrink-0"
        >
          <RefreshCcw className={cn('size-4', refreshing && 'animate-spin')} />
          Refresh data
        </Button>
      </div>

      <nav className="flex gap-1 border-b border-zinc-800/80 pb-px">
        {adminNav.map(item => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition -mb-px',
                  isActive
                    ? 'border-cyan-400 text-white'
                    : 'border-transparent text-zinc-400 hover:text-zinc-200',
                )
              }
            >
              <Icon className="size-4" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <Outlet />
    </div>
  );
}
